/**
 * /portal/notifications — in-portal notification feed.
 *
 * T-047 (the full notification system: dispatcher + transactional email via
 * the OQ-04 / ARCH-010 provider, retry/backoff) is an ADR candidate and is
 * blocked on the email-provider ADR plus a server-side dispatcher that is
 * OUTSIDE Phase G file ownership. The ONE part of T-047 that is buildable and
 * subscriber-facing here is the "/portal/notifications renders the feed" AC
 * line: a read of the notifications table (RLS notifications_self_select
 * scopes to recipient_user_id = auth.uid()), newest first, with a
 * mark-as-read affordance (notifications_self_update permits the read_at
 * write on the own row). The dispatch + email side is flagged in IMPL-NOTES
 * for T-047 as an ADR-blocked server follow-up.
 */

import { useCallback, useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  fetchOwnNotifications,
  markNotificationRead,
  type PortalNotification,
} from '@/lib/portal';
import { AsyncPanel, type AsyncState } from '@/components/portal/AsyncPanel';

export const Route = createFileRoute('/portal/notifications')({
  component: NotificationsPage,
});

const TITLE: Record<string, string> = {
  onboarding_completed: 'Onboarding received',
  welcome_view_ready: 'Eric has read your context',
  task_pulled: 'A task was pulled',
  deliverable_ready: 'A deliverable is ready',
  task_approved: 'Task approved',
  waitlist_update: 'Waitlist update',
  pause_confirmed: 'Subscription paused',
  cancel_confirmed: 'Subscription canceled',
  revision_requested: 'Revision requested',
  report_ready: 'A progression report is ready',
  // Surface F (PRD-005 / ARCH-028, IR-T-10). Titles verbatim from COPY §E.
  // input_requested is recipient-facing; input_submitted is operator-facing
  // (an operator viewing their own feed). The {firstName} token in the
  // operator title is filled from the payload below (humanTitle), falling back
  // to a name-free phrasing when the payload carries none.
  input_requested: 'Eric sent you a few questions',
};

/**
 * The contextClause phrase (COPY §E) derived from the request's context_type,
 * carried on the notification payload by the IR RPCs (migration 0035). Used in
 * the input_submitted body. Returns null for an unknown/absent context so the
 * body degrades to its leading phrase without a trailing clause.
 */
function contextClause(payload: Record<string, unknown>): string | null {
  const ctx = payload['context_type'];
  if (ctx === 'task') return 'on your task';
  if (ctx === 'os') return 'in their engagement';
  if (ctx === 'general') return 'as a general request';
  return null;
}

function humanTitle(type: string): string {
  if (type === 'input_submitted') {
    // Operator-facing row (COPY §E). {firstName} is not on the payload, so we
    // use the name-free phrasing; the operator read surface (IR-2b) resolves
    // the recipient name where it has it.
    return 'Your questions were answered';
  }
  return TITLE[type] ?? type.replace(/_/g, ' ');
}

/**
 * The optional body line for a notification row (COPY §E). Only the two
 * input-request types render a body in this feed today; everything else shows
 * title + time as before. The IR RPC payloads (migration 0036) carry
 * intro_one_line + question_count, so input_requested renders the operator
 * intro verbatim (locked fallback when none) and input_submitted renders the
 * {n} answer count.
 */
function bodyFor(
  type: string,
  payload: Record<string, unknown>,
): string | null {
  if (type === 'input_requested') {
    const intro =
      typeof payload.intro_one_line === 'string'
        ? payload.intro_one_line.trim()
        : '';
    return intro.length > 0
      ? intro
      : 'A few quick questions before he keeps moving.';
  }
  if (type === 'input_submitted') {
    const clause = contextClause(payload);
    const n =
      typeof payload.question_count === 'number' ? payload.question_count : null;
    const count = n !== null ? `${n} ${n === 1 ? 'answer' : 'answers'}` : 'Answers';
    return clause
      ? `${count} ${clause}. Read them and use them in the work.`
      : `${count}. Read them and use them in the work.`;
  }
  return null;
}

/** The action label for a row (COPY §E), or null when the row has none. */
function actionFor(type: string): string | null {
  if (type === 'input_requested') return 'Answer';
  if (type === 'input_submitted') return 'Read';
  return null;
}

function NotificationsPage() {
  const [state, setState] = useState<AsyncState<PortalNotification[]>>({
    status: 'loading',
  });

  const load = useCallback(async () => {
    try {
      const notes = await fetchOwnNotifications();
      setState({ status: 'ready', data: notes });
    } catch {
      setState({
        status: 'error',
        message: 'Could not load your notifications. Try again in a moment.',
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function markRead(id: string) {
    try {
      await markNotificationRead(id);
      await load();
    } catch {
      // Non-fatal; the unread state simply persists.
    }
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--color-text-mute)]">
          Updates
        </p>
        <h1 className="mb-1 font-display text-h2 italic leading-tight text-[color:var(--color-navy)]">
          What&apos;s happened.
        </h1>
        <p className="text-sm text-[color:var(--color-text-mute)]">
          Everything that has happened, newest first.
        </p>
      </div>

      <AsyncPanel state={state} loadingLabel="Loading notifications...">
        {(notes) =>
          notes.length === 0 ? (
            <p className="text-sm text-[color:var(--color-text-mute)]">
              Nothing here yet.
            </p>
          ) : (
            <ul className="divide-y divide-[color:var(--color-rule)]">
              {notes.map((n) => {
                const unread = n.read_at === null;
                return (
                  <li key={n.id} className="flex items-start gap-4 py-4">
                    <span
                      className={[
                        'mt-0.5 inline-flex shrink-0 items-center rounded-sm px-2 py-0.5 text-[10px] font-medium uppercase leading-none tracking-[0.05em]',
                        unread
                          ? 'bg-[color:var(--color-rose)] text-[color:var(--color-navy)]'
                          : 'border border-[color:var(--color-line-dark)] bg-[color:var(--color-linen)] text-[color:var(--color-navy)]',
                      ].join(' ')}
                    >
                      {unread ? 'New' : 'Read'}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium leading-snug text-[color:var(--color-navy)]">
                        {humanTitle(n.type)}
                      </p>
                      {bodyFor(n.type, n.payload) ? (
                        <p className="mt-0.5 text-[13px] leading-relaxed text-[color:var(--color-text-mute)] text-pretty">
                          {bodyFor(n.type, n.payload)}
                        </p>
                      ) : null}
                      <time className="text-xs text-[color:var(--color-text-mute)]">
                        {new Date(n.created_at).toLocaleString('en-US')}
                      </time>
                      {actionFor(n.type) ? (
                        <p className="mt-1 text-[12px] font-medium text-[color:var(--color-navy)] underline underline-offset-2">
                          {actionFor(n.type)}
                        </p>
                      ) : null}
                    </div>
                    {unread ? (
                      <button
                        type="button"
                        onClick={() => markRead(n.id)}
                        className="shrink-0 text-xs font-medium text-[color:var(--color-navy)] underline-offset-4 hover:underline"
                      >
                        Mark read
                      </button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )
        }
      </AsyncPanel>
    </div>
  );
}
